import { SearchFilter } from '@wellesley/openapi'
import { useGetApiV1SearchQuery } from '../../api/search'
import useDebounceValue from 'hooks/useDebounceValue'
import { SEARCH_LIMIT } from '../../const/searches'
import { SearchData } from '../../types/search'
import { SearchFilterValue } from '../../types/filters'
import { isHashSearch } from '../../helpers/isHashSearch'

type UseSearchProps = {
  searchInput: string
  groupId?: string
  selectedFilters: SearchFilterValue[]
  debounceMs?: number
  limit?: number
}

const filtersMap = {
  posts: 'posts',
  people: 'users',
  groups: 'groups',
  events: 'events',
  tags: 'tags',
  anywhere: undefined,
}

const getApiFilter = (filters: string[]): SearchFilter[] | undefined => {
  if (!filters || filters.length === 0) return undefined
  const mapped = filters
    .map((filter) => filtersMap[filter as keyof typeof filtersMap])
    .filter(Boolean) as SearchFilter[]
  return mapped.length === 0 ? undefined : mapped
}

const hasNoResultsInFilter = (
  data: SearchData | undefined,
  filter: SearchFilterValue[],
): boolean => {
  if (!data || filter.length === 0) return false

  const propertiesToCheck = filter.map(
    (f) => filtersMap[f as keyof typeof filtersMap],
  )

  const validProperties = propertiesToCheck.filter(Boolean)
  if (validProperties.length === 0) return false

  return validProperties.every((prop) => {
    const key = prop as keyof SearchData
    return !data[key] || (Array.isArray(data[key]) && data[key].length === 0)
  })
}

export const useSearch = ({
  searchInput,
  selectedFilters,
  debounceMs = 300,
  groupId,
  limit = SEARCH_LIMIT,
}: UseSearchProps) => {
  const debouncedSearchInput = useDebounceValue(searchInput, debounceMs)

  const cleanedSearchInput = isHashSearch(debouncedSearchInput)
    ? debouncedSearchInput.replace(/^#/, '')
    : debouncedSearchInput

  const { data: mainData, isFetching: mainLoading } = useGetApiV1SearchQuery(
    {
      q: cleanedSearchInput,
      filter: getApiFilter(selectedFilters),
      ...(groupId ? { groupId } : {}),
      limit,
    },
    { skip: !cleanedSearchInput || cleanedSearchInput.length < 2 },
  )

  const shouldFetchElsewhere = hasNoResultsInFilter(
    mainData as unknown as SearchData | undefined,
    selectedFilters,
  )

  const { data: elsewhereData, isFetching: elsewhereLoading } =
    useGetApiV1SearchQuery(
      {
        q: cleanedSearchInput,
        filter: getApiFilter(selectedFilters),
        limit,
      },
      { skip: !shouldFetchElsewhere || !cleanedSearchInput },
    )

  return {
    mainData: mainData as unknown as SearchData | undefined,
    elsewhereData: elsewhereData as unknown as SearchData | undefined,
    loading: mainLoading || elsewhereLoading,
  }
}
